import { cn } from "@/lib/utils";
import {
  formatOrderSummaryQty,
  type CustomJobSummaryCardViewModel,
} from "@/modules/custom-jobs/domain/build-custom-job-summary-card";
import { forwardRef, type ReactNode } from "react";

type CustomJobSummaryCardProps = {
  summary: CustomJobSummaryCardViewModel;
  className?: string;
  id?: string;
};

function SummaryPill({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex shrink-0 items-center rounded-full bg-secondary px-3 py-1 font-medium text-secondary-foreground text-sm">
      {children}
    </span>
  );
}

function SummaryField({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "rounded-2xl bg-muted px-4 py-3 text-base text-foreground leading-relaxed",
        className,
      )}
    >
      {children}
    </div>
  );
}

function SummaryBar({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-2xl bg-secondary px-4 py-2.5 text-center font-medium text-secondary-foreground text-sm">
      {children}
    </div>
  );
}

function TotalRow({
  label,
  value,
  strong,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className={strong ? "font-medium" : "text-muted-foreground"}>
        {label}
      </span>
      <span
        className={cn(
          "shrink-0 tabular-nums",
          strong && "font-display text-lg text-secondary",
        )}
      >
        {value}
      </span>
    </div>
  );
}

export const CustomJobSummaryCard = forwardRef<
  HTMLDivElement,
  CustomJobSummaryCardProps
>(function CustomJobSummaryCard({ summary, className, id }, ref) {
  return (
    <div
      ref={ref}
      id={id}
      className={cn(
        "flex w-full max-w-88 flex-col gap-3 rounded-3xl border border-border bg-card p-4 shadow-md print:max-w-[80mm] print:rounded-none print:border-0 print:p-3 print:shadow-none",
        className,
      )}
    >
      <SummaryBar>{summary.shopName}</SummaryBar>

      <div className="flex flex-col gap-1 text-center">
        <p className="font-display text-lg">ສະຫຼຸບງານສັ່ງເຮັດ</p>
        <p className="font-medium text-base tabular-nums tracking-wide">
          {summary.jobNumber}
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-start gap-3">
          <SummaryPill>ລູກຄ້າ</SummaryPill>
          <div className="min-w-0 flex-1 pt-0.5">
            <p className="font-medium text-base">{summary.customerName}</p>
            {summary.phone ? (
              <p className="mt-0.5 text-muted-foreground text-sm tabular-nums">
                {summary.phone}
              </p>
            ) : null}
          </div>
        </div>

        <SummaryField className="text-sm">
          {summary.dueDateLabel ? (
            <p>ນັດຮັບ: {summary.dueDateLabel}</p>
          ) : (
            <p>ນັດຮັບ: —</p>
          )}
          <p className="mt-1 text-muted-foreground">{summary.paymentLabel}</p>
        </SummaryField>

        {summary.note ? (
          <SummaryField className="whitespace-pre-line text-sm">
            {summary.note}
          </SummaryField>
        ) : null}
      </div>

      <div className="flex flex-col gap-2">
        <p className="font-medium text-secondary text-sm">ລາຍການ</p>
        <div className="flex flex-col gap-1.5">
          {summary.itemGroups.map((group) => (
            <div
              key={group.productName}
              className="rounded-xl border border-border/70 bg-muted/50 px-3 py-2 text-sm"
            >
              <p className="font-medium leading-snug">{group.productName}</p>
              <ul className="mt-1.5 flex flex-col gap-1 border-secondary/15 border-l pl-2.5">
                {group.variants.map((variant) => (
                  <li
                    key={`${variant.variantName}:${variant.quantity}:${variant.amountLabel}`}
                    className="flex items-start justify-between gap-2"
                  >
                    <span className="min-w-0 text-muted-foreground leading-snug">
                      {variant.variantName}
                    </span>
                    <span className="shrink-0 text-right text-muted-foreground tabular-nums">
                      {formatOrderSummaryQty(
                        variant.quantity,
                        variant.unitLabel,
                      )}{" "}
                      = {variant.amountLabel}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <SummaryField className="flex flex-col gap-2 py-3">
        <TotalRow label="ຍອດລວມລາຍການ" value={summary.subtotal} />
        {summary.discount ? (
          <TotalRow label="ສ່ວນລົດ" value={summary.discount} />
        ) : null}
        <div className="border-secondary/20 border-t pt-2">
          <TotalRow label="ລວມທັງໝົດ" value={summary.total} strong />
        </div>
      </SummaryField>

      <p className="text-center text-muted-foreground text-xs">
        ຂອບໃຈທີ່ໃຊ້ບໍລິການ 🌻
      </p>
    </div>
  );
});
